import { useAlert } from '@dwelvio/react-alert';
import { useParams } from 'react-router';
import { useApp } from '../../components/app-context';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import {
  AddFileAttachmentToDocumentDocument,
  AddFileAttachmentToDocumentMutation,
  AddFileAttachmentToDocumentMutationVariables,
  ConfirmFileAttachmentUploadedToDocumentDocument,
  ConfirmFileAttachmentUploadedToDocumentMutation,
  ConfirmFileAttachmentUploadedToDocumentMutationVariables,
  FileRefFragment,
  GetDocumentDocument,
  GetDocumentQuery,
  GetDocumentQueryVariables,
  UpdateDocumentInContractDocument,
  UpdateDocumentInContractMutation,
  UpdateDocumentInContractMutationVariables,
  UpdateDocumentInUnitDocument,
  UpdateDocumentInUnitMutation,
  UpdateDocumentInUnitMutationVariables,
} from '../../generated/graphql';
import { useCallback, useEffect } from 'react';
import { BasePage } from '../../components/base-page';
import { Stack } from '@mui/material';
import { DeletedItemBanner } from '../../components/deleted-item-banner';
import { DocumentData, DocumentEditor } from '../../components/document-editor';
import { uploadFile } from '../../lib/upload-files';
import { fileContentDisposition } from '@dwelvio/shared';

export function DocumentPage() {
  const app = useApp();
  const { show } = useAlert();
  const { documentId } = useParams();

  const [fetchDocument, { data, loading, error }] = useLazyQuery<GetDocumentQuery, GetDocumentQueryVariables>(
    GetDocumentDocument,
  );
  const [updateDocumentInUnit, { loading: updateLoading }] = useMutation<
    UpdateDocumentInUnitMutation,
    UpdateDocumentInUnitMutationVariables
  >(UpdateDocumentInUnitDocument, {
    onError: (e: Error) => {
      show.error('Nepodarilo sa aktualizovať dokument.', e.message);
    },
  });
  const [updateDocumentInContract, { loading: updateLoadingC }] = useMutation<
    UpdateDocumentInContractMutation,
    UpdateDocumentInContractMutationVariables
  >(UpdateDocumentInContractDocument, {
    onError: (e: Error) => {
      show.error('Nepodarilo sa aktualizovať dokument.', e.message);
    },
  });

  const [addAttachment] = useMutation<
    AddFileAttachmentToDocumentMutation,
    AddFileAttachmentToDocumentMutationVariables
  >(AddFileAttachmentToDocumentDocument);

  const [confirmAttachment] = useMutation<
    ConfirmFileAttachmentUploadedToDocumentMutation,
    ConfirmFileAttachmentUploadedToDocumentMutationVariables
  >(ConfirmFileAttachmentUploadedToDocumentDocument);

  const document = data?.getDocument;
  const documentLocked = !!document?.deletedAt;

  const handleAddAttachment = useCallback(
    async (file: File): Promise<FileRefFragment | undefined> => {
      if (!documentId) {
        return;
      }

      try {
        const headers = {
          'Content-Disposition': fileContentDisposition(file.name),
        };
        const results = await uploadFile(
          file,
          (file) =>
            addAttachment({
              variables: {
                documentId,
                type: 'da',
                input: { filename: file.name, mimeType: file.type, size: file.size },
              },
            }),
          (addData) => addData.addFileAttachmentToDocument.uploadUrl,
          (file, addData) =>
            confirmAttachment({
              variables: { documentId, fileId: addData.addFileAttachmentToDocument.fileId, size: file.size },
            }),
          headers,
        );

        return results.data?.confirmFileAttachmentUploadedToDocument;
      } catch (error) {
        show.error('Nepodarilo sa nahrať prílohu.', (error as Error).message);
        return;
      }
    },
    [addAttachment, confirmAttachment, show],
  );

  const handleUpdate = useCallback(
    async (input: DocumentData) => {
      if (document && document.unitId) {
        await updateDocumentInUnit({
          variables: {
            unitId: document.unitId,
            documentId: document.id,
            input,
          },
        });
      }
      if (document && document.contractId) {
        await updateDocumentInContract({
          variables: {
            contractId: document.contractId,
            documentId: document.id,
            input,
          },
        });
      }
    },
    [documentId, updateDocumentInUnit, show, document],
  );

  useEffect(() => {
    if (documentId) {
      fetchDocument({
        variables: { documentId },
      });
    }
  }, [documentId, fetchDocument]);

  return (
    <BasePage
      title={`Dokument - ${data?.getDocument?.title || ''}`}
      loading={loading || !data?.getDocument}
      errorMsg={error ? 'Nepodarilo sa načítať detaily dokumentu.' : undefined}
    >
      {document && documentId && (
        <Stack spacing={2} sx={{ marginTop: '1rem' }}>
          {document.deletedAt && (
            <DeletedItemBanner text={`Tento dokument bol zmazaný ${app.formatDate(document.deletedAt)}.`} />
          )}
          <DocumentEditor
            document={document}
            onChange={handleUpdate}
            updateLoading={updateLoading || updateLoadingC}
            disabled={documentLocked}
            addFileAttachment={handleAddAttachment}
            addUrlAttachment={function (url: string): Promise<FileRefFragment> {
              throw new Error('Function not implemented.');
            }}
          />
        </Stack>
      )}
    </BasePage>
  );
}
